/**
 * MatrixGrid — the signature drug × drug pairwise grid for the full matrix.
 *
 * - Cells coloured by `VERDICT_TONE[cell.verdict]` (no_data → neutral).
 * - Desktop: ~32px cells, hover tooltip (verdict + pair + source/outdated).
 * - Mobile: ≥44px cells, tap-to-select (HIG floor).
 * - Sticky row + column headers; both-axis scroll on the container.
 * - Diagonal = self (inert, hatched). Missing pair = muted / no-data.
 * - Sentinel rows ("(all carriers)", "(any other drug)") carry blanket
 *   verdicts and are labelled distinctly — they are not normal selectable
 *   drugs, so their headers don't act as drug-selectors.
 *
 * Axes are asymmetric on purpose: `rowDrugs` and `colDrugs` are independent
 * arrays so the board can narrow ONE axis (search/focus) while keeping the
 * other broad (full universe or category-scoped). When no search/category is
 * active they are the same list and the grid is symmetric N×N.
 *
 * `visibleTones` is a presentation filter — cells whose tone is not in the
 * set keep their fill but dim to a low opacity and stop responding to hover
 * (so the structure stays intact for spatial memory while the de-selected
 * tones recede visually). Selection state and tap targets are unaffected.
 *
 * Triangular rendering: when the `triangle` prop is true (symmetric, no
 * search narrowing), only the lower triangle (colIndex < rowIndex) carries
 * cells — every pair appears exactly once, the way correlation matrices
 * are conventionally drawn. Above-diagonal cells render as empty space
 * (no border, no fill). The diagonal stays as the hatched self cell.
 *
 * Sentinels are ALWAYS column-only — they're stripped from rowDrugs by
 * the board before they reach this component. Their blanket-rule context
 * appears as the leftmost two cells of every drug row. So the triangle
 * rule applies only to the drug × drug subgrid; sentinel cols always render.
 *
 * Selection is lifted to the board. The header callbacks are axis-aware:
 *   - `onSelectRowDrug` for clicks on a row-header (left side of the table)
 *   - `onSelectColDrug` for clicks on a column-header (top of the table)
 * Cell clicks fire `onSelectCell(row, col, cell)` — atomic both-axis assignment.
 *
 * Axis-aware callbacks matter because a Y-site compatibility check is
 * `matrix[i, j]` — one drug from the row axis AND one from the column axis.
 * Two row-header clicks is "I'm browsing along the row axis, replace my pick",
 * not "I want to check these two against each other". Mixing the axes would
 * fake a pair the user didn't ask for. The board owns the two slot ids
 * (one per axis); MatrixGrid is just the interaction surface.
 */

import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import type {
  CompatibilityBoardState,
  CompatibilityCell,
  CompatibilityDrug,
  VerdictTone,
} from '@frontend/components/ysite/types'
import { getCell, VERDICT_TONE } from '@frontend/components/ysite/types'
import { TONE_FILL } from '@frontend/components/ysite/verdict.ui'
import { shortLabel, splitLabel } from '@frontend/components/ysite/label'

interface MatrixGridProps {
  state: CompatibilityBoardState
  /**
   * The live search query — used only to interpolate the no-results message
   * ("No drug matches '{query}'"). Was previously hardcoded to '' here, so
   * the empty state always read `No drug matches ""` no matter what the nurse
   * typed. The board owns the query; we just echo it back in the message.
   */
  query: string
  /** Rows axis — typically narrowed by search ("what is X compatible with"). */
  rowDrugs: CompatibilityDrug[]
  /** Columns axis — typically the full universe (or category-scoped). */
  colDrugs: CompatibilityDrug[]
  /** Tones the user wants to see at full opacity. Others dim but stay in place. */
  visibleTones: Set<VerdictTone>
  /**
   * Collapse symmetric N×N to lower triangle. True when rowDrugs represents
   * the full non-sentinel slice of colDrugs (no search narrowing). The board
   * computes this — MatrixGrid doesn't try to infer it from array identity
   * since rowDrugs and colDrugs never share identity now that sentinels are
   * always stripped from rows.
   */
  triangle: boolean
  selectedDrugId: string | null
  selectedPair: [string, string] | null
  onSelectCell: (a: CompatibilityDrug, b: CompatibilityDrug, cell: CompatibilityCell) => void
  /** Row-header click — sets the row-axis slot. */
  onSelectRowDrug: (drug: CompatibilityDrug) => void
  /** Column-header click — sets the column-axis slot. */
  onSelectColDrug: (drug: CompatibilityDrug) => void
}

interface Tip {
  x: number
  y: number
  verdict: string
  pair: string
}

export function MatrixGrid({
  state,
  query,
  rowDrugs,
  colDrugs,
  visibleTones,
  triangle,
  selectedDrugId,
  selectedPair,
  onSelectCell,
  onSelectRowDrug,
  onSelectColDrug,
}: MatrixGridProps) {
  const { t } = useTranslation()
  const [tip, setTip] = useState<Tip | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)

  const activeIds = new Set<string>()
  if (selectedDrugId) activeIds.add(selectedDrugId)
  if (selectedPair) {
    activeIds.add(selectedPair[0])
    activeIds.add(selectedPair[1])
  }

  // Empty state — only when there is genuinely nothing to render on either
  // axis. The asymmetric split means search collapsing rows to zero still
  // shows the empty state; a populated row axis with full columns renders
  // normally (strip).
  if (rowDrugs.length === 0 || colDrugs.length === 0) {
    // A non-empty query that matches no matrix drug means the nurse searched
    // for something outside this institution's matrix (e.g. fentanyl in a
    // carrier-compat dataset). The board fires a Swiss-medication (SAI) lookup
    // for it into the side panel, so the grid empty state points there rather
    // than dead-ending on "no match". With an empty query rowDrugs can't be
    // zero (it mirrors colDrugs), so the noResults branch is the defensive
    // fallback, not the live path.
    const trimmed = query.trim()
    return (
      <div className="flex h-40 items-center justify-center px-6 text-center text-[15px] text-neutral-500">
        {trimmed
          ? t('compatibility.search.offMatrix', { query: trimmed })
          : t('compatibility.search.noResults', { query: trimmed })}
      </div>
    )
  }

  // Triangle mode: collapse the symmetric N×N to a lower triangle so each
  // pair appears once. The board owns the decision and tells us via prop.
  // We still need a column-index lookup keyed against colDrugs so the
  // triangle skip can compare positions on the original symmetric axis.
  // Sentinel cols stay rendered (sentinels were already filtered from rows
  // upstream, so the cleanup-once rule applies on the drug × drug subgrid).
  const colIndexById = new Map<string, number>()
  if (triangle) {
    for (let i = 0; i < colDrugs.length; i++) {
      const drug = colDrugs[i]
      if (drug) colIndexById.set(drug.id, i)
    }
  }
  const rowIndexInCols = (rowDrug: CompatibilityDrug) =>
    colIndexById.get(rowDrug.id) ?? -1

  function showTip(
    e: React.MouseEvent,
    a: CompatibilityDrug,
    b: CompatibilityDrug,
    cell: CompatibilityCell,
  ) {
    setTip({
      x: e.clientX,
      y: e.clientY,
      verdict: t(`compatibility.verdicts.${cell.verdict}`),
      pair: `${shortLabel(a)} ↔ ${shortLabel(b)}`,
    })
  }

  return (
    // Both-axis scroll inside a single overflow container = sticky cells
    // freeze on both axes (Excel-style). The wrapper is `relative` with
    // explicit `min-h-0 min-w-0 flex-1` so the overflow child can be
    // sized absolutely against it via `inset-0` — this is what reliably
    // constrains BOTH dimensions when the parent is a flex column. A
    // plain `h-full w-full` on a flex item is too easily defeated by
    // the flex algorithm growing the item to content width, breaking
    // horizontal scroll.
    <div className="relative min-h-0 min-w-0 flex-1">
      <div
        ref={scrollRef}
        className="compat-matrix-scroll absolute inset-0 overflow-auto rounded-[18px] border border-[var(--hairline)] bg-[var(--canvas)]"
        // The grid is interactive content; allow keyboard scroll.
        tabIndex={0}
        role="region"
        aria-label={t('compatibility.matrix.title')}
      >
        <table className="border-separate border-spacing-[3px]">
          <thead>
            <tr>
              {/* Corner — sticky on both axes */}
              <th className="sticky left-0 top-0 z-30 bg-[var(--canvas)]" />
              {colDrugs.map((d) => {
                const isSentinel = d.sentinel
                const active = activeIds.has(d.id)
                return (
                  <th
                    key={d.id}
                    scope="col"
                    className="sticky top-0 z-20 h-[124px] overflow-visible bg-[var(--canvas)] p-0 align-bottom"
                  >
                    <button
                      type="button"
                      disabled={isSentinel}
                      onClick={() => !isSentinel && onSelectColDrug(d)}
                      title={d.label}
                      className={cn(
                        // 45° diagonal header — anchored at the cell's bottom-left
                        // (origin-bottom-left) and rotated up-to-the-right. Far
                        // shorter than the old full-height vertical-rl column, so many
                        // more matrix ROWS are visible at once. A single diagonal line
                        // (nowrap); the full name rides on `title` for the rare long
                        // one. Tailwind transforms (not raw CSS) so `active:scale-95`
                        // composes via --tw-rotate / --tw-scale-* instead of replacing
                        // the rotation.
                        'absolute bottom-1 left-1 origin-bottom-left -rotate-45 whitespace-nowrap pl-0.5 text-[12px] leading-none',
                        'transition active:scale-95',
                        isSentinel
                          ? 'cursor-default font-semibold italic text-neutral-400'
                          : active
                            ? 'font-semibold text-primary-600'
                            : 'text-neutral-700 hover:text-primary-600',
                      )}
                    >
                      {shortLabel(d)}
                    </button>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {rowDrugs.map((rowDrug) => {
              const rowActive = activeIds.has(rowDrug.id)
              const { name, concentration } = rowDrug.sentinel
                ? { name: rowDrug.label, concentration: '' }
                : splitLabel(rowDrug.label)
              // Triangle reference position for this row in the symmetric
              // axis (colDrugs). Only consulted when triangle is on; in
              // non-triangle strip mode it's unused.
              const rowRefIndex = triangle ? rowIndexInCols(rowDrug) : -1
              return (
                <tr key={rowDrug.id}>
                  <th
                    scope="row"
                    className="sticky left-0 z-10 bg-[var(--canvas)] p-0 text-right"
                  >
                    <button
                      type="button"
                      disabled={rowDrug.sentinel}
                      onClick={() => !rowDrug.sentinel && onSelectRowDrug(rowDrug)}
                      title={rowDrug.label}
                      className={cn(
                        'block max-w-[200px] truncate pr-2.5 text-right text-[13px] leading-tight',
                        'transition active:scale-95',
                        rowDrug.sentinel
                          ? 'cursor-default font-semibold italic text-neutral-400'
                          : rowActive
                            ? 'font-semibold text-primary-600'
                            : 'text-neutral-700 hover:text-primary-600',
                      )}
                    >
                      {name}
                      {concentration && (
                        <span className="block text-[11px] font-normal text-neutral-400">
                          {concentration}
                        </span>
                      )}
                    </button>
                  </th>

                  {colDrugs.map((colDrug) => {
                    // Triangle skip: above-diagonal cells in the symmetric
                    // case render as empty space. Sentinel COLUMNS are kept
                    // (sentinel rows are filtered out above), so blanket
                    // rules appear once per drug row at the leftmost cells.
                    if (triangle && !colDrug.sentinel) {
                      const colIndex = colIndexById.get(colDrug.id) ?? -1
                      if (colIndex > rowRefIndex) {
                        return <td key={colDrug.id} aria-hidden className="p-0" />
                      }
                    }

                    if (rowDrug.id === colDrug.id) {
                      return (
                        <td key={colDrug.id} className="p-0">
                          <div
                            aria-hidden
                            title={t('compatibility.matrix.self')}
                            className="h-11 w-11 rounded-[6px] bg-[repeating-linear-gradient(45deg,var(--color-neutral-100)_0_6px,var(--color-neutral-200)_6px_12px)] sm:h-8 sm:w-8"
                          />
                        </td>
                      )
                    }

                    const cell = getCell(state, rowDrug.id, colDrug.id)
                    if (!cell) {
                      // The "no data" cell is the neutral `muted` tone — the
                      // tone filter dims it too when the user has hidden
                      // 'muted', so the structure stays consistent across the
                      // cells-with-data and cells-without-data paths.
                      const noDataDimmed = !visibleTones.has('muted')
                      return (
                        <td key={colDrug.id} className="p-0">
                          <div
                            title={t('compatibility.matrix.noData')}
                            aria-label={t('compatibility.matrix.ariaCell', {
                              a: shortLabel(rowDrug),
                              b: shortLabel(colDrug),
                              verdict: t('compatibility.matrix.noData'),
                            })}
                            className={cn(
                              'h-11 w-11 rounded-[6px] bg-neutral-100 transition-opacity sm:h-8 sm:w-8',
                              noDataDimmed && 'opacity-15',
                            )}
                          />
                        </td>
                      )
                    }

                    const tone = VERDICT_TONE[cell.verdict]
                    const isSelected =
                      !!selectedPair &&
                      ((selectedPair[0] === rowDrug.id && selectedPair[1] === colDrug.id) ||
                        (selectedPair[1] === rowDrug.id && selectedPair[0] === colDrug.id))
                    const lineActive =
                      !isSelected &&
                      !!selectedDrugId &&
                      (selectedDrugId === rowDrug.id || selectedDrugId === colDrug.id)
                    // Tone filter: when the cell's tone is not in the visible
                    // set, fade it (preserving structure) and suppress the
                    // hover tooltip — the user is explicitly saying "I don't
                    // want to look at these right now". Selection still wins
                    // visually because outline/ring sit ABOVE opacity.
                    const dimmed = !visibleTones.has(tone)

                    return (
                      <td key={colDrug.id} className="p-0">
                        <button
                          type="button"
                          onClick={() => onSelectCell(rowDrug, colDrug, cell)}
                          onMouseEnter={(e) => {
                            if (dimmed) return
                            showTip(e, rowDrug, colDrug, cell)
                          }}
                          onMouseMove={(e) => {
                            if (dimmed) return
                            setTip((p) => (p ? { ...p, x: e.clientX, y: e.clientY } : p))
                          }}
                          onMouseLeave={() => setTip(null)}
                          aria-label={t('compatibility.matrix.ariaCell', {
                            a: shortLabel(rowDrug),
                            b: shortLabel(colDrug),
                            verdict: t(`compatibility.verdicts.${cell.verdict}`),
                          })}
                          className={cn(
                            'relative h-11 w-11 rounded-[6px] transition active:scale-95 sm:h-8 sm:w-8',
                            TONE_FILL[tone],
                            'hover:outline hover:outline-2 hover:outline-offset-1 hover:outline-primary-500',
                            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary-focus',
                            isSelected &&
                              'outline outline-2 outline-offset-2 outline-primary-500 ring-4 ring-primary-500/20',
                            lineActive && 'outline-dashed outline-1 -outline-offset-1 outline-primary-500/45',
                            dimmed && 'opacity-15 hover:outline-none',
                          )}
                        />
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Hover tooltip — pointer-fine devices only; touch uses tap-to-select.
          Rendered inert so it never blocks a tap. */}
      {tip && (
        <div
          role="tooltip"
          style={{ left: tip.x + 12, top: tip.y - 8 }}
          className="pointer-events-none fixed z-50 hidden max-w-[260px] rounded-[10px] border border-[var(--hairline)] bg-[var(--canvas)] px-3 py-2.5 shadow-lg [@media(hover:hover)]:block"
        >
          <div className="text-[13px] font-semibold text-neutral-900">{tip.verdict}</div>
          <div className="mt-1 text-[12px] text-neutral-600">{tip.pair}</div>
          <div className="mt-1.5 text-[11px] text-neutral-400">
            {`${t('compatibility.datasetLabel')} ${state.source_doc.replace('/', ' ')}`}
          </div>
        </div>
      )}
    </div>
  )
}
